import { ListHeading } from "@/components/shared/list-header";
import { Badge, RadioGroup, View } from "@/components/ui";
import { ClipboardText, Cube } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import type { FrequencyOption } from "@/components/shared/service-radio-item/types";
import { useBookingState } from "@/store/booking/useBookingState";
import {
  ElderCareFrequencyOptionItems,
  ElderCareFrequencyOptions,
} from "@/constants/booking";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ServiceRadioItem } from "@/components/shared/service-radio-item";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import {
  ClientServiceProfileFragmentFragmentDoc,
  PackageDepartmentEnum,
  PackageRecurrenceEnum,
} from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";
import { InlineServiceCard } from "@/containers/profile/service-profile/inline-service-card";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { getFragmentData } from "@/__generated__";
import { transformServiceProfileToDetail } from "@/containers/profile/service-profile/lib";
import { AuthModals } from "@/types/users";
import { ElderProfileModal } from "./elder-profile-modal";
import { HelpCard } from "../pet-grooming-booking/help-card";
import { Divider } from "@/components/shared/divider";

export const ElderCareBooking = () => {
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const {
    data: {
      packageDetailsFilter,
      elderCareProfileInfo,
      isEmptyErrorElderProfile,
    },
    setBookingState,
  } = useBookingState();

  const {
    showModal,
    data: { userInfo },
  } = useAuthState();

  const {
    fetch: getAllClientPackage,
    data: allPackagesData,
    loading: isLoadingPackages,
  } = useAllClientPackageDetailsStore();

  const clientId = userInfo.id;
  const elderCareSelectedFrequencyOption = useMemo(() => {
    return (
      ElderCareFrequencyOptions.flat().find(
        (opt) => opt.frequency === packageDetailsFilter.frontEndFrequency,
      ) || ElderCareFrequencyOptions[0][0]
    );
  }, [packageDetailsFilter.frontEndFrequency]);

  useEffect(() => {
    getAllClientPackage({
      requestPayload: {
        filters: {
          repeatEvery: [
            PackageRecurrenceEnum.Week,
            PackageRecurrenceEnum.Fortnight,
            PackageRecurrenceEnum.AdHoc,
          ],
          department: PackageDepartmentEnum.ElderCare,
          active: true,
        },
      },
    });
  }, [getAllClientPackage]);

  useEffect(() => {
    if (allPackagesData && elderCareSelectedFrequencyOption) {
      const pkg = allPackagesData.find(
        (p) => p.code === elderCareSelectedFrequencyOption.code,
      );
      if (pkg) {
        setBookingState({ selectedPackageIds: [pkg.id] });
      }
    }
  }, [allPackagesData, elderCareSelectedFrequencyOption, setBookingState]);

  useEffect(() => {
    setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        repeatEvery: [elderCareSelectedFrequencyOption.repeatEvery],
        repeatEveryTimes: elderCareSelectedFrequencyOption.repeatEveryTimes,
        frontEndFrequency: elderCareSelectedFrequencyOption.frequency,
      },
    });
  }, []);

  const onChooseFrequency = useCallback(
    (item: FrequencyOption) => {
      const pkg = allPackagesData?.find(
        (p) => p.code === elderCareSelectedFrequencyOption.code,
      );

      setBookingState({
        packageDetailsFilter: {
          ...packageDetailsFilter,
          repeatEvery: [item.repeatEvery],
          repeatEveryTimes: item.repeatEveryTimes,
          frontEndFrequency: item.frequency,
        },
      });

      if (pkg) {
        setBookingState({ selectedPackageIds: [pkg.id] });
      }
    },
    [allPackagesData, packageDetailsFilter, setBookingState],
  );

  const { data: profileData } = useClientServiceProfileByFiltersQuery("elder");
  const profiles =
    profileData?.clientServiceProfileByFilters.clientServiceProfiles ?? [];

  useEffect(() => {
    if (profiles.length > 0) {
      const defaultProfile = profiles.find(
        (profile) =>
          getFragmentData(ClientServiceProfileFragmentFragmentDoc, profile).primary,
      );
      if (defaultProfile) {
        const profile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          defaultProfile,
        );
        setBookingState({
          elderCareProfileInfo: {
            id: profile.id,
            ...transformServiceProfileToDetail("elder", profile),
          },
          selectedServiceProfileId: profile.id,
        });
      }
    }
  }, [profiles]);

  const handleChange = () => {
    if (!clientId) {
      showModal(AuthModals.LOGIN);
      return;
    }

    setOpenProfileModal(true);
  };

  return (
    <View>
      <View className="p-4">
        <View className="flex flex-col gap-2">
          <View className="flex flex-row gap-3">
            <ListHeading title="Pre-Service Assessment" icon={ClipboardText} />
            <Badge variant="success" size="sm">
              Required
            </Badge>
          </View>
          <Typography variant="body-sm" color="foreground-intermediate">
            Helps us assess your needs and assign the right care aide.
          </Typography>

          <View className="my-3 flex flex-col gap-2">
            {profiles.length && elderCareProfileInfo ? (
              <InlineServiceCard
                type="elder"
                profile={elderCareProfileInfo}
                onChange={() => setOpenProfileModal(true)}
              />
            ) : (
              <InlineServiceCard
                type="add"
                label="Choose the tasks you need help with"
                onChange={handleChange}
              />
            )}
            {isEmptyErrorElderProfile && (
              <Typography
                variant="caption-md"
                color="danger"
                className="italic"
              >
                To proceed, please provide the required details.
              </Typography>
            )}
          </View>
        </View>

        <Divider className="h-0.5px mb-3" />

        <View>
          <View className="flex flex-col gap-2">
            <View className="flex flex-row gap-3">
              <ListHeading title="Care Frequency" icon={Cube} />
            </View>
            <Typography variant="body-sm" color="foreground-intermediate">
              Select your preferred cleaning frequency, you can always change
              this again after the booking.
            </Typography>
          </View>
        </View>

        {isLoadingPackages && (
          <View className="mt-6 flex items-center justify-center py-8">
            <Typography variant="body-sm" color="foreground-intermediate">
              Loading packages...
            </Typography>
          </View>
        )}

        <View className="mt-6 flex flex-col gap-4 border-b border-secondary-border pb-4">
          {!isLoadingPackages &&
            ElderCareFrequencyOptionItems.map((groupItem) => (
              <View key={groupItem.title} className="flex flex-col gap-4">
                {groupItem.title && (
                  <Typography variant="body-sm">{groupItem.title}</Typography>
                )}
                <RadioGroup
                  value={elderCareSelectedFrequencyOption.title}
                  onValueChange={(value) => {
                    const option = groupItem.options.find(
                      (item) => item.title === value,
                    ) as FrequencyOption;
                    onChooseFrequency(option);
                  }}
                >
                  <View className="flex flex-col gap-4 overflow-visible">
                    {groupItem.options.map((item) => (
                      <ServiceRadioItem
                        showSelectIcon={false}
                        key={item.title}
                        title={item.title}
                        subTitle={`$${item.price}/hr`}
                        selected={
                          elderCareSelectedFrequencyOption.title === item.title
                        }
                        value={item.title}
                        showSparkle
                        onSelect={() => onChooseFrequency(item)}
                      />
                    ))}
                  </View>
                </RadioGroup>
              </View>
            ))}
        </View>
        <ElderProfileModal
          open={openProfileModal}
          setOpen={setOpenProfileModal}
        />
      </View>
      <HelpCard
        name="elder-care"
        subTitle="Let us match you with the perfect helper!"
      />
    </View>
  );
};
